import Ionicons from "@expo/vector-icons/Ionicons";
import { useRouter } from "expo-router";
import {
  Pressable,
  StyleSheet,
  View,
} from "react-native";

import { Modal } from "@/components/ui/app-modal";
import { useTranslation } from "react-i18next";

import { ThemedText } from "@/components/themed-text";
import { BlurredBackdrop } from "@/components/ui/blurred-backdrop";
import { useTheme } from "@/hooks/use-theme";
import { useTrialOffer, type TrialTrigger } from "@/lib/trial-offer";

export function PlusFeatureUpsell({
  visible,
  title,
  intro,
  body,
  body2,
  cta,
  dismiss,
  trigger = "plus_feature_attempted",
  onDismiss,
  onConfirm,
}: {
  visible: boolean;
  title: string;
  intro?: string;
  body: string;
  body2: string;
  cta: string;
  dismiss: string;
  trigger?: TrialTrigger;
  onDismiss: () => void;
  onConfirm?: () => void;
}) {
  const c = useTheme();
  const { t } = useTranslation();
  const router = useRouter();
  const trialOffer = useTrialOffer(trigger, visible);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onDismiss}
    >
      <View style={styles.screen}>
        <BlurredBackdrop
          accessibilityLabel={dismiss}
          onPress={onDismiss}
          style={styles.backdrop}
        />
        <View
          style={[
            styles.card,
            { backgroundColor: c.surface, borderColor: c.border },
          ]}
        >
          <View style={[styles.badge, { backgroundColor: c.backgroundSelected }]}>
            <Ionicons name="sparkles" size={20} color={c.tint} />
          </View>

          <ThemedText variant="subtitle" style={styles.title}>
            {title}
          </ThemedText>

          {intro ? (
            <ThemedText
              variant="body"
              style={[styles.body, { color: c.textMuted }]}
            >
              {intro}
            </ThemedText>
          ) : null}

          <ThemedText
            variant="body"
            style={[styles.body, { color: c.textMuted }]}
          >
            {body}
          </ThemedText>
          <ThemedText
            variant="body"
            style={[styles.body, { color: c.textMuted }]}
          >
            {body2}
          </ThemedText>

          <Pressable
            accessibilityRole="button"
            onPress={() => {
              onConfirm?.() ?? onDismiss();
              router.push("/pricing");
            }}
            style={[styles.cta, { backgroundColor: c.tint }]}
          >
            <ThemedText
              variant="bodyStrong"
              style={[styles.ctaText, { color: c.onTint }]}
            >
              {trialOffer
                ? t("billing.trial.gateCta", { days: trialOffer.days })
                : cta}
            </ThemedText>
          </Pressable>

          <Pressable
            accessibilityRole="button"
            onPress={onDismiss}
            style={styles.dismiss}
          >
            <ThemedText variant="caption" style={{ color: c.textMuted }}>
              {dismiss}
            </ThemedText>
          </Pressable>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, alignItems: "center", justifyContent: "center" },
  backdrop: { position: "absolute", top: 0, right: 0, bottom: 0, left: 0 },
  card: {
    width: "88%",
    maxWidth: 420,
    borderRadius: 20,
    borderWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: 22,
    paddingTop: 22,
    paddingBottom: 14,
    alignItems: "center",
  },
  badge: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 14,
  },
  title: { textAlign: "center", marginBottom: 12 },
  body: { textAlign: "center", marginBottom: 12, lineHeight: 20 },
  cta: {
    alignSelf: "stretch",
    borderRadius: 14,
    paddingVertical: 13,
    alignItems: "center",
    marginTop: 4,
  },
  ctaText: { textAlign: "center" },
  dismiss: { paddingVertical: 12, paddingHorizontal: 16, marginTop: 2 },
});
